<template>
    <RegularModal ref="modal" :prevent-close-by-background="true" @closed="onClosed">
        <template v-slot:title>
            <span>{{ translate("post_wizard_onboarding.modal.title") }}</span>
        </template>
        <template v-slot:body>
            <p class="mb-4">{{ translate("post_wizard_onboarding.modal.intro") }}</p>
            <p>{{ translate("post_wizard_onboarding.modal.checklist_hint") }}</p>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="onSkip">
                    {{ translate("post_wizard_onboarding.modal.btn_skip") }}
                </button>
                <button class="button is-primary" @click="onStartTour">
                    <span class="icon"><i class="fas fa-compass"></i></span>
                    <span>{{ translate("post_wizard_onboarding.modal.btn_start_tour") }}</span>
                </button>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref } from "vue";
    import RegularModal from "../../Common/RegularModal.vue";
    import { translate } from "../../Common/translations";
    import { useEditEventStore } from "../editEventStore";
    import { useAnalytics } from "../../Common/Analytics/useAnalytics";
    import federationsApi from "../../Api/federationsApi";
    import { setupChecklistApi } from "../../Api/setupChecklistApi";
    import { useOnboardingTour } from "./useOnboardingTour";
    import history from "../../Common/history";

    const store = useEditEventStore();
    const modal = ref<InstanceType<typeof RegularModal> | null>(null);
    const { trackEvent, events } = useAnalytics();
    const historyInstance = history.getInstance(window);

    function recordDecision(decision: "started_tour" | "skipped" | "completed_tour") {
        const federationId = store.metadata?.federationId;
        if (!federationId) {
            return;
        }

        // Best-effort: don't surface an error toast if this fails, the modal/tour already progressed for the organizer.
        federationsApi.recordPostWizardOnboardingDecision({ federationId, decision }).catch(() => {});
    }

    function open() {
        trackEvent(events.Organizer.OnboardingModalShown);
        modal.value?.open();
    }

    function close() {
        modal.value?.close();
    }

    function onClosed() {
        // Covers the native close paths (delete button, Escape) which don't go through
        // onSkip/onStartTour: still strip the query param so a reload doesn't reopen the
        // modal, without recording a decision the organizer didn't make.
        historyInstance.setQueryParam("action", null);
    }

    function onSkip() {
        historyInstance.setQueryParam("action", null);
        recordDecision("skipped");
        trackEvent(events.Organizer.OnboardingSkipped);
        close();
    }

    function onStartTour() {
        historyInstance.setQueryParam("action", null);
        recordDecision("started_tour");
        trackEvent(events.Organizer.OnboardingTourStarted);
        close();

        setupChecklistApi
            .getSetupChecklist(store.eventId)
            .then(checklist => {
                const hasCategories = checklist.items.find(item => item.itemKey === "categories")?.done ?? false;
                startTour(hasCategories);
            })
            .catch(() => startTour(false));
    }

    function startTour(hasCategories: boolean) {
        const tour = useOnboardingTour({
            hasCategories,
            onComplete: () => recordDecision("completed_tour"),
        });
        tour.start();
    }

    defineExpose({ open, close });
</script>
